/**
 * Phase 11.5b — /app/knowledge-graph — Knowledge Graph explorer.
 *
 * Server-side filtering: `GET /api/v1/kg/nodes?label_search=&node_type=`
 * (label_search is a case-insensitive substring match added in 11.5b).
 *
 * List → row click → open existing `KnowledgeGraphDrawer` (react-flow
 * canvas). Multiselect of node_type + debounced label search.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { useLocale } from "../../lib/i18n";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import KnowledgeGraphDrawer, {
  nodeColorClass,
} from "../../components/app/KnowledgeGraphDrawer";
import { Network, Search } from "lucide-react";

const NODE_TYPES = [
  "Campaign", "Creative", "LandingPage", "Experiment", "Outcome",
  "ContentPost", "Region", "Audience", "Channel", "Offer", "Funnel",
];

const COPY = {
  de: {
    title: "Knowledge Graph",
    subtitle:
      "Alle Entitäten deines Marketing-Wissensgraphen. Klicke eine Zeile für die Nachbarschaftsansicht.",
    filter_type: "Knotentyp",
    filter_all: "Alle Typen",
    label_search_ph: "Nach Label suchen (z. B. Wuppertal)",
    empty:
      "Noch keine Knowledge Graph Knoten. Führe eine Kampagne oder LP-Publish aus, um Knoten zu erstellen.",
    count: (n) => `${n} Knoten`,
    open: "Öffnen",
    col_type: "Typ",
    col_label: "Label",
    col_subject: "Subject Ref",
    col_created: "Erstellt",
    loading: "Lade Knoten…",
  },
  en: {
    title: "Knowledge Graph",
    subtitle:
      "All entities in your marketing knowledge graph. Click a row to open the neighborhood view.",
    filter_type: "Node type",
    filter_all: "All types",
    label_search_ph: "Search by label (e.g. Wuppertal)",
    empty:
      "No Knowledge Graph nodes yet. Publish a campaign or landing page to create nodes.",
    count: (n) => `${n} nodes`,
    open: "Open",
    col_type: "Type",
    col_label: "Label",
    col_subject: "Subject Ref",
    col_created: "Created",
    loading: "Loading nodes…",
  },
};

// Debounce hook — resolves the value after `ms` of inactivity.
function useDebounced(value, ms = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

function fmtDate(iso, locale) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(
      locale === "de" ? "de-DE" : "en-US",
      { dateStyle: "short", timeStyle: "short" });
  } catch (e) { return iso; }
}

export default function KnowledgeGraph() {
  const { locale } = useLocale();
  const t = COPY[locale] || COPY.en;
  const [nodeTypes, setNodeTypes] = useState(() => {
    // Phase 18c — hydrate multiselect from URL ?node_type=strategy,tactic
    const p = new URLSearchParams(window.location.search);
    const raw = p.get("node_type");
    return raw ? raw.split(",").filter(Boolean) : [];
  });
  const [labelSearch, setLabelSearch] = useState("");
  const debouncedSearch = useDebounced(labelSearch, 250);
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [typePickerOpen, setTypePickerOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (nodeTypes.length > 0) params.set("node_type", nodeTypes.join(","));
    if (debouncedSearch) params.set("label_search", debouncedSearch);
    params.set("limit", "200");
    // Phase 18c — sync URL so refresh preserves the picker state.
    const urlParams = new URLSearchParams(window.location.search);
    if (nodeTypes.length > 0) urlParams.set("node_type", nodeTypes.join(","));
    else urlParams.delete("node_type");
    const q = urlParams.toString();
    const next = window.location.pathname + (q ? `?${q}` : "");
    if (next !== window.location.pathname + window.location.search) {
      window.history.replaceState(null, "", next);
    }
    api.get(`/kg/nodes?${params.toString()}`)
      .then((r) => { if (alive) setNodes(r.data?.items || []); })
      .catch(() => { if (alive) setNodes([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [nodeTypes, debouncedSearch]);

  const openRow = (id) => { setSelectedId(id); setDrawerOpen(true); };

  const rows = useMemo(() => nodes, [nodes]);

  return (
    <div className="p-6 space-y-5" data-testid="knowledge-graph-page">
      <header className="flex items-start gap-3">
        <div className="size-10 rounded-md bg-foreground text-background grid place-items-center">
          <Network className="size-5" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t.title}</h1>
          <p className="text-sm text-muted-foreground">{t.subtitle}</p>
        </div>
      </header>

      {/* Filter toolbar */}
      <Card data-testid="kg-explorer-toolbar">
        <CardContent className="pt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2 space-y-1.5">
            <label className="text-xs text-muted-foreground">
              {t.label_search_ph}
            </label>
            <div className="relative">
              <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="kg-explorer-label-search"
                value={labelSearch}
                onChange={(e) => setLabelSearch(e.target.value)}
                placeholder={t.label_search_ph}
                className="pl-9"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground">
              {t.filter_type}
            </label>
            <div className="relative" data-testid="kg-explorer-type-filter">
              <button
                type="button"
                data-testid="kg-explorer-type-picker-button"
                onClick={() => setTypePickerOpen((o) => !o)}
                className="w-full h-9 rounded-md border bg-background px-3 text-sm flex items-center justify-between hover:bg-accent"
              >
                <span className="truncate text-left">
                  {nodeTypes.length === 0
                    ? t.filter_all
                    : `${nodeTypes.length} selected`}
                </span>
                <span className="text-muted-foreground ml-2">▾</span>
              </button>
              {typePickerOpen && (
                <div
                  data-testid="kg-explorer-type-picker-popover"
                  className="absolute z-20 mt-1 w-full rounded-md border bg-popover shadow-md p-2 space-y-1"
                  onMouseLeave={() => setTypePickerOpen(false)}
                >
                  {nodeTypes.length > 0 && (
                    <button
                      type="button"
                      data-testid="kg-explorer-type-picker-clear"
                      onClick={() => setNodeTypes([])}
                      className="w-full text-left text-xs text-muted-foreground hover:text-foreground py-1 px-2 rounded hover:bg-accent"
                    >
                      Clear selection
                    </button>
                  )}
                  {NODE_TYPES.map((nt) => {
                    const checked = nodeTypes.includes(nt);
                    return (
                      <label
                        key={nt}
                        data-testid={`kg-explorer-type-opt-${nt}`}
                        className="flex items-center gap-2 py-1 px-2 rounded hover:bg-accent cursor-pointer text-sm"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            setNodeTypes((prev) =>
                              checked
                                ? prev.filter((v) => v !== nt)
                                : [...prev, nt]);
                          }}
                          className="size-4"
                        />
                        <span>{nt}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            {nodeTypes.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1"
                       data-testid="kg-explorer-type-chips">
                {nodeTypes.map((nt) => (
                  <span
                    key={nt}
                    className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs bg-accent"
                    data-testid={`kg-explorer-type-chip-${nt}`}
                  >
                    {nt}
                    <button
                      type="button"
                      aria-label={`Remove ${nt}`}
                      onClick={() =>
                        setNodeTypes((prev) => prev.filter((v) => v !== nt))}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Node table */}
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-medium text-muted-foreground"
                     data-testid="kg-explorer-count">
            {t.count(rows.length)}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div data-testid="kg-explorer-loading"
                 className="p-8 text-sm text-muted-foreground">
              {t.loading}
            </div>
          ) : rows.length === 0 ? (
            <div data-testid="kg-explorer-empty"
                 className="p-10 text-sm text-muted-foreground text-center">
              {t.empty}
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="kg-explorer-table">
              <thead className="text-xs uppercase text-muted-foreground border-b bg-muted/30">
                <tr>
                  <th className="text-left px-4 py-2 w-40">{t.col_type}</th>
                  <th className="text-left px-4 py-2">{t.col_label}</th>
                  <th className="text-left px-4 py-2 w-40">{t.col_subject}</th>
                  <th className="text-left px-4 py-2 w-44">{t.col_created}</th>
                  <th className="w-20 px-2 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((n) => (
                  <tr
                    key={n.id}
                    data-testid={`kg-explorer-row-${n.id}`}
                    className="hover:bg-accent/40 transition-colors cursor-pointer"
                    onClick={() => openRow(n.id)}
                  >
                    <td className="px-4 py-2">
                      <Badge
                        variant="outline"
                        className={"font-mono text-[10px] " +
                                   nodeColorClass(n.node_type)}
                        data-testid={`kg-explorer-row-${n.id}-type`}
                      >
                        {n.node_type}
                      </Badge>
                    </td>
                    <td className="px-4 py-2 truncate max-w-[400px]"
                        data-testid={`kg-explorer-row-${n.id}-label`}>
                      {n.label || "—"}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-muted-foreground"
                        title={n.subject_ref || ""}>
                      {n.subject_ref ? String(n.subject_ref).slice(0, 8) : "—"}
                    </td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">
                      {fmtDate(n.created_at, locale)}
                    </td>
                    <td className="px-2 py-2 text-right">
                      <Button
                        variant="ghost" size="sm"
                        data-testid={`kg-explorer-row-${n.id}-open`}
                        onClick={(e) => { e.stopPropagation(); openRow(n.id); }}
                      >{t.open}</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <KnowledgeGraphDrawer
        nodeId={selectedId}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />
    </div>
  );
}
